import React, { useState, useEffect } from 'react';
import {
  getGradeSubmissionStatus,
  publishGradesToStudents,
  resetExamSubjectGrades,
  unpublishGradesFromStudents,
  fetchActiveClasses,
  type GradeSubmissionStatus,
  type SchoolClassDto,
} from '../../api/axios';
import { useSchool } from '../../context/SchoolContext';
import { StatusBadge } from './StatusBadge';
import {
  formatGradingTerm,
  gradingTermSelectOptions,
  defaultGradingTermYears,
} from '../../utils/gradingTerm';
import { classLevelWord, formatClassLevelLabel } from '../../utils/classLevelLabel';
import { fetchSchoolSettings } from '../../api/axios';

export const AdminGradeReviewSection: React.FC = () => {
  const { currentSchool, getSchoolInfo } = useSchool();
  const schoolInfo = getSchoolInfo(currentSchool);
  
  const [classes, setClasses] = useState<SchoolClassDto[]>([]);
  const [selectedForm, setSelectedForm] = useState(1);
  const [selectedTerm, setSelectedTerm] = useState(() =>
    formatGradingTerm(1, new Date().getFullYear())
  );

  useEffect(() => {
    fetchSchoolSettings(currentSchool).then(s => {
      setSelectedTerm(formatGradingTerm(s.current_term, Number(s.current_academic_year)));
    }).catch(() => {});
  }, [currentSchool]);
  const [submissionStatus, setSubmissionStatus] = useState<GradeSubmissionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    loadClasses();
  }, [currentSchool]);

  useEffect(() => {
    if (classes.length > 0) {
      loadSubmissionStatus();
    }
  }, [selectedForm, selectedTerm, classes.length, currentSchool]);

  const loadClasses = async () => {
    try {
      const data = await fetchActiveClasses({ school: currentSchool });
      setClasses(data);
      if (data.length > 0) {
        setSelectedForm(data[0].level);
      }
    } catch (error) {
      console.error('Failed to load classes', error);
    }
  };

  const loadSubmissionStatus = async () => {
    setLoading(true);
    try {
      const data = await getGradeSubmissionStatus(selectedForm, selectedTerm, currentSchool);
      setSubmissionStatus(data);
    } catch (error) {
      console.error('Failed to load submission status', error);
      alert('Failed to load grade submission status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishSubject = async (subjectId: number, subjectName: string) => {
    if (
      !window.confirm(
        `Publish ${subjectName} grades to students?\n\nThis sets exam grades as official: students see them, and report cards (including class standing and points) can include them for this term.`
      )
    ) {
      return;
    }

    try {
      await publishGradesToStudents({
        form: selectedForm,
        term: selectedTerm,
        school: currentSchool,
        subject_id: subjectId,
      });

      alert(
        `✓ ${subjectName} grades published successfully!\n\nStudents can now see their grades.`
      );
      await loadSubmissionStatus();
    } catch (error) {
      console.error('Failed to publish grades', error);
      alert(`❌ Failed to publish grades.\n\nError: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  const handleUnpublishSubject = async (subjectId: number, subjectName: string) => {
    if (
      !window.confirm(
        `Unpublish ${subjectName} grades from students?\n\nStudents will no longer be able to see these grades until you publish again.`
      )
    ) {
      return;
    }

    try {
      const response = await unpublishGradesFromStudents({
        subject_id: subjectId,
        form: selectedForm,
        term: selectedTerm,
        school: currentSchool,
      });
      alert(`✓ Unpublished.\n\nUpdated: ${response.updated_count} grade(s).`);
      await loadSubmissionStatus();
    } catch (error) {
      console.error('Failed to unpublish grades', error);
      alert(`❌ Failed to unpublish.\n\nError: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  const handleResetSubject = async (subjectId: number, subjectName: string) => {
    if (
      !window.confirm(
        `RESET (DELETE) ALL ${subjectName} exam grades for ${formatClassLevelLabel(currentSchool, selectedForm)}, ${selectedTerm}?\n\nThis will permanently delete the saved marks for this subject/term/class level.\nTeachers must re-enter and submit again.\n\nType OK to confirm.`
      )
    ) {
      return;
    }

    try {
      const response = await resetExamSubjectGrades({
        subject_id: subjectId,
        form: selectedForm,
        term: selectedTerm,
        school: currentSchool,
      });
      alert(`✓ Reset complete.\n\nDeleted: ${response.deleted_count} grade(s).`);
      await loadSubmissionStatus();
    } catch (error) {
      console.error('Failed to reset subject grades', error);
      alert(`❌ Failed to reset.\n\nError: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  const handlePublishAllSubmitted = async () => {
    if (!submissionStatus) return;

    const submittedSubjects = submissionStatus.subjects.filter(
      (s) => s.submitted_to_admin && !s.admin_published
    );

    if (submittedSubjects.length === 0) {
      alert('⚠️ No subjects to publish!\n\nAll submitted subjects have already been published.');
      return;
    }

    if (
      !window.confirm(
        `Publish ALL submitted grades to students?\n\nThis will publish ${submittedSubjects.length} subject(s) for ${formatClassLevelLabel(currentSchool, selectedForm)}, ${selectedTerm}.\n\nStudents will see their exam grades, and report cards can use these marks for rankings and overall points.`
      )
    ) {
      return;
    }

    try {
      await publishGradesToStudents({
        form: selectedForm,
        term: selectedTerm,
        school: currentSchool,
      });

      alert(
        `✓ All submitted grades published successfully!\n\n${submittedSubjects.length} subject(s) published.\nStudents can now see their grades.`
      );
      await loadSubmissionStatus();
    } catch (error) {
      console.error('Failed to publish all grades', error);
      alert(`❌ Failed to publish all grades.\n\nError: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  const submittedCount = submissionStatus?.subjects.filter((s) => s.submitted_to_admin).length || 0;
  const publishedCount = submissionStatus?.subjects.filter((s) => s.admin_published).length || 0;
  const pendingCount = submittedCount - publishedCount;
  const gradingTermOptions = gradingTermSelectOptions(defaultGradingTermYears());

  return (
    <div style={{ 
      padding: '32px 40px',
      flex: 1,
      background: '#f8f9fa',
      minHeight: '100%'
    }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <h2
            style={{
              fontSize: 28,
              fontWeight: 700,
              margin: 0,
              color: '#333',
            }}
          >
            🔍 Grade Review & Publishing
          </h2>
          <div
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={() => setShowHelp(true)}
            onMouseLeave={() => setShowHelp(false)}
          >
            <button
              type="button"
              aria-label="Learn more about Grade Review and Publishing"
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                border: '1px solid #93c5fd',
                background: '#eff6ff',
                color: '#1d4ed8',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'help',
              }}
            >
              ?
            </button>
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                width: 320,
                maxWidth: '80vw',
                padding: '12px 14px',
                borderRadius: 10,
                border: '1px solid #bfdbfe',
                background: '#fff',
                boxShadow: '0 10px 25px rgba(15,23,42,0.15)',
                color: '#1f2937',
                fontSize: 13,
                zIndex: 30,
                opacity: showHelp ? 1 : 0,
                visibility: showHelp ? 'visible' : 'hidden',
                transform: showHelp ? 'translateY(0)' : 'translateY(-4px)',
                transition: 'opacity 0.16s ease, transform 0.16s ease',
                pointerEvents: 'none',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use this page</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Review submitted subjects for the selected form and term.</li>
                <li>Publish when grades are approved for student visibility and reporting.</li>
                <li>Use unpublish or reset only for corrections and re-entry workflows.</li>
              </ul>
            </div>
          </div>
        </div>
        <p style={{ fontSize: 15, color: '#666', margin: 0 }}>
          Review teacher submissions and publish grades to students for {schoolInfo.name}
        </p>
      </div>

      {/* Form and Term Selector */}
      <div
        style={{
          background: '#fff',
          padding: 24,
          borderRadius: 12,
          border: '1px solid #e0e0e0',
          marginBottom: 32,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {/* Form Selector */}
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 700,
                color: '#666',
                marginBottom: 8,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {classLevelWord(currentSchool)} / Class
            </label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {classes.length === 0 ? (
                <div style={{ padding: '14px 20px', color: '#999', fontSize: 14 }}>
                  No classes found for {schoolInfo.name}
                </div>
              ) : (
                classes.map((classItem) => {
                  const isSelected = selectedForm === classItem.level;
                  return (
                    <button
                      key={classItem.id}
                      onClick={() => setSelectedForm(classItem.level)}
                      style={{
                        flex: classes.length <= 4 ? 1 : undefined,
                        minWidth: classes.length > 4 ? 100 : undefined,
                        padding: '14px 20px',
                        background: isSelected
                          ? schoolInfo.theme.primaryColor
                          : '#fff',
                        color: isSelected ? '#fff' : '#333',
                        border: isSelected ? 'none' : '2px solid #e0e0e0',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontWeight: isSelected ? 700 : 600,
                        fontSize: 16,
                        transition: 'all 0.2s',
                        boxShadow: isSelected
                          ? `0 4px 12px ${schoolInfo.theme.primaryColor}50`
                          : '0 2px 4px rgba(0,0,0,0.08)',
                      }}
                    >
                      {classItem.name}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Term Selector */}
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 700,
                color: '#666',
                marginBottom: 8,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Term
            </label>
            <select
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 20px',
                borderRadius: 8,
                border: '2px solid #e0e0e0',
                background: '#fff',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {gradingTermOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary Stats */}
        {submissionStatus && (
          <div
            style={{
              marginTop: 24,
              paddingTop: 24,
              borderTop: '2px solid #f0f0f0',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 16,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#333', marginBottom: 4 }}>
                {submissionStatus.total_subjects}
              </div>
              <div style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>Total Subjects</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: pendingCount > 0 ? '#ffc107' : '#999',
                  marginBottom: 4,
                }}
              >
                {pendingCount}
              </div>
              <div style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>Awaiting Review</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#17a2b8', marginBottom: 4 }}>
                {submittedCount}
              </div>
              <div style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>Submitted</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#28a745', marginBottom: 4 }}>
                {publishedCount}
              </div>
              <div style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>Published</div>
            </div>
          </div>
        )}
      </div>

      {/* Publish All Button */}
      {pendingCount > 0 && (
        <div style={{ marginBottom: 24, textAlign: 'right' }}>
          <button
            onClick={handlePublishAllSubmitted}
            style={{
              padding: '14px 32px',
              background: '#059669',
              color: '#fff',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 15,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: '0 4px 12px rgba(40,167,69,0.3)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(40,167,69,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(40,167,69,0.3)';
            }}
          >
            📤 Publish All Submitted Grades ({pendingCount})
          </button>
        </div>
      )}

      {/* Subject List */}
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
          <div>Loading submissions for {schoolInfo.name}...</div>
        </div>
      ) : !submissionStatus || submissionStatus.subjects.length === 0 ? (
        <div
          style={{
            padding: 60,
            textAlign: 'center',
            background: '#fff',
            borderRadius: 12,
            border: '2px dashed #e0e0e0',
          }}
        >
          <div style={{ fontSize: 64, marginBottom: 16 }}>📚</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#666', marginBottom: 8 }}>
            No Subjects Found
          </div>
          <div style={{ fontSize: 14, color: '#999' }}>
            No subjects have been assigned for this form and term in {schoolInfo.name}.
          </div>
        </div>
      ) : (
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #e0e0e0',
            overflow: 'visible',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <div className="tableScroll">
          {/* Table Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 120px 120px 120px 260px',
              padding: '16px 24px',
              background: schoolInfo.theme.primaryColor,
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              minWidth: 980,
            }}
          >
            <div>Subject</div>
            <div>Teacher</div>
            <div style={{ textAlign: 'center' }}>Students</div>
            <div style={{ textAlign: 'center' }}>Grades</div>
            <div style={{ textAlign: 'center' }}>Status</div>
            <div style={{ textAlign: 'center' }}>Action</div>
          </div>

          {/* Subject Rows */}
          {submissionStatus.subjects.map((subject, index) => {
            const isEven = index % 2 === 0;
            const canPublish = subject.submitted_to_admin && !subject.admin_published;

            return (
              <div
                key={subject.subject_id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 2fr 120px 120px 120px 260px',
                  padding: '18px 24px',
                  alignItems: 'center',
                  background: isEven ? '#fafafa' : '#fff',
                  borderBottom: '1px solid #e0e0e0',
                  minWidth: 980,
                }}
              >
                {/* Subject */}
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>
                    {subject.subject_name}
                  </div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                    {subject.subject_code}
                  </div>
                </div>

                {/* Teacher */}
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#666' }}>
                    {subject.teacher_name}
                  </div>
                </div>

                {/* Students */}
                <div style={{ textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#333' }}>
                  {subject.total_students}
                </div>

                {/* Grades */}
                <div style={{ textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#333' }}>
                  {subject.saved_grades}
                </div>

                {/* Status */}
                <div style={{ textAlign: 'center' }}>
                  {subject.admin_published ? (
                    <StatusBadge status="published" size="small" />
                  ) : subject.submitted_to_admin ? (
                    <StatusBadge status="submitted" size="small" />
                  ) : subject.saved_grades > 0 ? (
                    <StatusBadge status="draft" size="small" />
                  ) : (
                    <StatusBadge status="pending" size="small" />
                  )}
                </div>

                {/* Action */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {canPublish && (
                      <button
                        onClick={() => handlePublishSubject(subject.subject_id, subject.subject_name)}
                        style={{
                          padding: '8px 14px',
                          background: '#28a745',
                          color: '#fff',
                          borderRadius: 6,
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: 800,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          transition: 'all 0.2s',
                          boxShadow: '0 2px 6px rgba(40,167,69,0.3)',
                        }}
                      >
                        Publish
                      </button>
                    )}

                    {subject.admin_published && (
                      <button
                        onClick={() => handleUnpublishSubject(subject.subject_id, subject.subject_name)}
                        style={{
                          padding: '8px 14px',
                          background: '#dc3545',
                          color: '#fff',
                          borderRadius: 6,
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: 800,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          boxShadow: '0 2px 6px rgba(220,53,69,0.25)',
                        }}
                      >
                        Unpublish
                      </button>
                    )}

                    {subject.saved_grades > 0 && (
                      <button
                        onClick={() => handleResetSubject(subject.subject_id, subject.subject_name)}
                        style={{
                          padding: '8px 14px',
                          background: '#7f1d1d',
                          color: '#fff',
                          borderRadius: 6,
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: 900,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                        }}
                      >
                        Reset
                      </button>
                    )}

                    {!canPublish && !subject.admin_published && subject.saved_grades === 0 && (
                      <div style={{ fontSize: 12, color: '#999', fontWeight: 700 }}>
                        —
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
};
